import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CountrySelect } from '@/components/ui/country-select'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { OtpInput } from '@/components/ui/OtpInput'
import { ProfileCardShell } from '@/components/ProfileCardShell'
import { apiErrorMessage } from '@/lib/api-error'
import {
  getCountriesApi,
  getPhoneStatusApi,
  linkPhoneApi,
  confirmLinkPhoneApi,
  changePhoneApi,
  confirmChangePhoneApi,
  unlinkPhoneApi,
  type Country,
  type PhoneStatus,
} from '@/lib/api'
import {
  ArrowLeftIcon,
  Loader2,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  SendIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  Trash2Icon,
} from 'lucide-react'

type Mode = 'idle' | 'edit' | 'otp'
type Action = 'link' | 'change'

/**
 * Self-service phone number management card (DESIGN_PhoneLogin.md §5.2).
 * Lets the logged-in user link, change, or remove their own phone number,
 * which then works as an SMS-OTP login credential.
 */
export function PhoneNumberCard({
  unlinkConfirmDelaySeconds = 3,
}: {
  /** Countdown before the remove-confirm button activates (0 disables it — used in tests). */
  unlinkConfirmDelaySeconds?: number
} = {}) {
  const [status, setStatus] = useState<PhoneStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)

  const [countries, setCountries] = useState<Country[]>([])
  const [selectedCountryId, setSelectedCountryId] = useState<number | ''>('')
  const [phone, setPhone] = useState('')

  const [mode, setMode] = useState<Mode>('idle')
  const [action, setAction] = useState<Action>('link')
  const [pendingE164, setPendingE164] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''))
  const [resendAfter, setResendAfter] = useState(0)

  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [confirmUnlink, setConfirmUnlink] = useState(false)
  const [unlinking, setUnlinking] = useState(false)

  const selectedCountry = countries.find((c) => c.country_id === selectedCountryId) ?? null

  const refreshStatus = async () => {
    const res = await getPhoneStatusApi()
    setStatus(res.data)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [statusRes, countriesRes] = await Promise.all([
          getPhoneStatusApi(),
          getCountriesApi(),
        ])
        if (cancelled) return
        setStatus(statusRes.data)
        // Only countries with an ISO code + dialing code are usable for SMS.
        const usable = countriesRes.data.filter((c) => c.iso_2 && c.dialing_code)
        setCountries(usable)
        // Pre-select the country of the currently linked number, if any.
        if (statusRes.data.phone_country_iso2) {
          const existing = usable.find((c) => c.iso_2 === statusRes.data.phone_country_iso2)
          if (existing) setSelectedCountryId(existing.country_id)
        }
      } catch {
        if (!cancelled) toast.error('Could not load phone settings')
      } finally {
        if (!cancelled) setStatusLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Resend countdown
  useEffect(() => {
    if (resendAfter <= 0) return
    const t = setInterval(() => setResendAfter((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(t)
  }, [resendAfter])

  const startFlow = (nextAction: Action) => {
    setAction(nextAction)
    setPhone('')
    setOtp(Array(6).fill(''))
    setResendAfter(0)
    // Pre-select the country of the currently linked number (change flow);
    // never carry over a stale pick from a previous attempt.
    const currentIso2 = status?.phone_country_iso2
    const existing = currentIso2 ? countries.find((c) => c.iso_2 === currentIso2) : undefined
    setSelectedCountryId(existing?.country_id ?? '')
    setMode('edit')
  }

  const cancelFlow = () => {
    setMode('idle')
    setOtp(Array(6).fill(''))
    setResendAfter(0)
  }

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!selectedCountry?.iso_2 || !selectedCountry.dialing_code) {
      toast.error('Please select a country')
      return
    }
    const nationalDigits = phone.replace(/\D/g, '')
    if (nationalDigits.length < 5) {
      toast.error('Enter a valid phone number')
      return
    }
    setSending(true)
    try {
      const res =
        action === 'link'
          ? await linkPhoneApi({ phone_number: nationalDigits, country_code: selectedCountry.iso_2 })
          : await changePhoneApi({ phone_number: nationalDigits, country_code: selectedCountry.iso_2 })
      // Canonical E.164 returned by the API — pass it straight to confirm.
      setPendingE164(res.data.phone_number)
      setMaskedPhone(res.data.phone_number_masked)
      setResendAfter(res.data.resend_after ?? 60)
      setOtp(Array(6).fill(''))
      setMode('otp')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to send code'))
      // 409s (number on another account / use-change-flow) desync local state.
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        void refreshStatus().catch(() => {})
      }
    } finally {
      setSending(false)
    }
  }

  const handleVerify = async () => {
    if (otp.some((d) => d === '') || !pendingE164) {
      toast.error('Enter the 6-digit code')
      return
    }
    setVerifying(true)
    try {
      if (action === 'link') {
        await confirmLinkPhoneApi({ phone_number: pendingE164, otp_code: otp.join('') })
      } else {
        await confirmChangePhoneApi({ phone_number: pendingE164, otp_code: otp.join('') })
      }
      toast.success(action === 'link' ? 'Phone number linked' : 'Phone number updated')
      await refreshStatus()
      setSelectedCountryId('')
      setMode('idle')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Verification failed'))
    } finally {
      setVerifying(false)
    }
  }

  const handleUnlink = async () => {
    setUnlinking(true)
    try {
      await unlinkPhoneApi()
      toast.success('Phone number removed')
      await refreshStatus()
      setSelectedCountryId('')
      setConfirmUnlink(false)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to remove phone number'))
    } finally {
      setUnlinking(false)
    }
  }

  return (
    <ProfileCardShell
      as="section"
      icon={PhoneIcon}
      iconClassName="text-muted-foreground"
      title="Phone number"
      right={
        status?.phone_verified && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-mint)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--brand-mint)]">
            <ShieldCheckIcon className="size-3" />
            Verified
          </span>
        )
      }
    >

      {statusLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading phone settings…
        </div>
      ) : mode === 'edit' ? (
        /* ── Country + number form ── */
        <form onSubmit={handleSend} className="flex flex-col gap-4" aria-label={`${action} phone number`}>
          <p className="text-sm text-muted-foreground">
            {action === 'link'
              ? 'Add a phone number to log in with SMS codes.'
              : 'Enter the new number. A verification code will be sent to it.'}
          </p>

          <div className="space-y-2">
            <Label htmlFor="phone-card-country">Country</Label>
            <CountrySelect
              id="phone-card-country"
              value={selectedCountryId}
              onChange={setSelectedCountryId}
              countries={countries}
              includeEmptyOption
              emptyLabel="Select a country"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone-card-number">Phone number</Label>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-10 shrink-0 items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground">
                {selectedCountry?.dialing_code ?? '+'}
              </span>
              <Input
                id="phone-card-number"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="801 234 5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s()+-]/g, ''))}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={sending} className="gap-1.5">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <SendIcon className="size-4" />}
              {sending ? 'Sending…' : 'Send code'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={cancelFlow} disabled={sending}>
              Cancel
            </Button>
          </div>
        </form>
      ) : mode === 'otp' ? (
        /* ── OTP step ── */
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-[var(--brand-mint)]/20 bg-[var(--brand-mint)]/5 p-4">
            <div className="flex items-center gap-2">
              <SmartphoneIcon className="size-4 text-[var(--brand-mint)]" />
              <p className="text-sm font-medium">Enter your verification code</p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Sent to {maskedPhone}. Code expires in 5 minutes.
            </p>
          </div>

          <OtpInput
            value={otp}
            onChange={setOtp}
            autoFocusFirst
            inputClassName="size-11 rounded-lg border border-border bg-background text-center text-lg font-semibold focus-visible:border-[var(--brand-mint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-mint)]/30"
          />

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handleVerify}
              disabled={verifying || otp.some((d) => d === '')}
              className="gap-1.5"
            >
              {verifying ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheckIcon className="size-4" />}
              {verifying ? 'Verifying…' : action === 'link' ? 'Verify & link' : 'Verify & change'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={cancelFlow} disabled={verifying}>
              Cancel
            </Button>
          </div>

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => setMode('edit')}
              className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeftIcon className="size-3" />
              Wrong number?
            </button>
            {resendAfter > 0 ? (
              <span className="text-muted-foreground/70">
                Resend code in 0:{String(resendAfter).padStart(2, '0')}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void handleSend()}
                className="font-medium text-[var(--brand-mint)] transition-colors hover:text-[var(--brand-mint)]/80"
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      ) : status?.phone_number ? (
        /* ── Linked state ── */
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10">
              <SmartphoneIcon className="size-5 text-[var(--brand-mint)]" />
            </span>
            <div>
              <p className="text-sm font-medium">{status.phone_number_masked ?? status.phone_number}</p>
              <p className="text-xs text-muted-foreground">
                Use this number to log in with an SMS code.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => startFlow('change')}>
              <PencilIcon className="size-4" />
              Change number
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={() => setConfirmUnlink(true)}
              disabled={!status.can_unlink}
            >
              <Trash2Icon className="size-4" />
              Remove
            </Button>
          </div>

          {!status.can_unlink && (
            <p className="text-xs text-amber-600">
              You can't remove your phone because it's your only login credential. Add an email and
              password first.
            </p>
          )}
        </div>
      ) : (
        /* ── No phone linked ── */
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            No phone number linked. Add one to log in with SMS codes as well as your email.
          </p>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => startFlow('link')}>
            <PlusIcon className="size-4" />
            Link phone
          </Button>
        </div>
      )}

      <DestructiveConfirm
        open={confirmUnlink}
        onClose={() => setConfirmUnlink(false)}
        title="Remove phone number?"
        description="You will no longer be able to log in with this phone number. Your email login still works."
        confirmLabel="Remove phone"
        onConfirm={handleUnlink}
        loading={unlinking}
        delaySeconds={unlinkConfirmDelaySeconds}
      />
    </ProfileCardShell>
  )
}
